import { Fragment } from "react";
import type { LookView, ThingView, ThreadView } from "../../shared/story";
import { Credit, TrustLinks } from "../trust";
import { WhereYouAre } from "../noirmap";
import { Care, Decision, VoiceShift, DeleteStory, Kept, SceneBreak, Weaving, waiting, More, SoundToggle, ChapterEnd, DreamNow, HurryHint, frayOf, inkOf, Plates, PlatesLink, Returned, SkinPicker, Unwoven, ReadingRoom, holdToHurry, Writer, linkify, quietFor, threadOrder, type ThemeProps } from "../common";
import { Margins } from "../v2/margins";
import { PenLine, Pull } from "../v2/pens";
import { LookActions } from "../v2/pocket";
import type { Shown, StoryUI } from "../useStory";
import "./nitrate.css";

/**
 * Nitrate: the story as a film (owner's pick of three directions, 2026-10-18; mock-up
 * https://claude.ai/artifact/LA1ArVrSHh3ihtdBmRZWWG). Black, silver and grain. Your lines are title cards between the
 * scenes, the narrator's prose plays under them, plates are stills on the strip, a chapter ends at the end of a reel.
 * The open threads run along the top as "now showing"; on a wide screen, beside the story with where you are.
 */

/** A title card holds a line this long in capitals; a longer one is set as plain text on the card. */
const CARD_CAPS = 140;
const QUIET = 3;

function Card({ t, meta }: { t: { input: string; kept?: string[] }; meta: boolean }) {
  if (meta) return <p className="nt-aside-line"><span className="nt-label">to the projectionist ·</span> {t.input}</p>;
  return (
    <div className={`nt-card${t.input.length > CARD_CAPS ? " nt-card-long" : ""}`}>
      <div className="nt-card-in">
        <svg className="nt-orn" width="88" height="8" viewBox="0 0 88 8" aria-hidden="true"><path d="M0 4h34M54 4h34M38 4l6-3 6 3-6 3z" /></svg>
        <p><span className="sr">You: </span><PenLine input={t.input} /></p>
        <Kept t={t} className="nt-kept" />
      </div>
    </div>
  );
}

function CloseUp({ s, l, onLook }: { s: StoryUI; l: LookView; onLook(name: string): void }) {
  return (
    <aside className="nt-closeup" aria-label={`Close-up: ${l.thing}`}>
      <span className="nt-label">close-up · {l.thing}</span>
      <p>{l.description}</p>
      <p className="nt-closeup-kids">
        <span className="nt-label">closer:</span>{" "}
        {l.children.map((ch) => (
          <button key={ch.id} type="button" className="lm-thing" title={ch.hint} onClick={() => onLook(ch.name)}>{ch.name}</button>
        ))}
      </p>
      <LookActions s={s} thing={l.thing} className="nt-label" />
    </aside>
  );
}

/** One turn: your title card, then the scene. */
function Scene({ s, t, things, looks, onLook, live }: {
  s: StoryUI; t: Shown; things: readonly ThingView[]; looks?: LookView[]; onLook: ((name: string) => void) | null;
  live?: { streaming: boolean; paused: boolean };
}) {
  const meta = t.intent === "meta";
  return (
    <article className="nt-turn" data-turn={t.n}>
      <SceneBreak t={t} className="nt-cut" mark="— cut to —" />
      <Card t={t} meta={meta} />
      <div className={`nt-scene${meta ? " nt-meta" : ""}`} {...(live ? holdToHurry(s) : {})}>
        <Returned t={t} className="nt-return" />
        <VoiceShift t={t} className="nt-voice-shift" />
        <Decision t={t} className="nt-decision" />
        <p data-prose={meta ? undefined : t.n} {...(live ? { "data-live": "" } : {})}>
          {meta && <span className="nt-label">projectionist · </span>}
          {linkify(t.prose, things, meta ? null : onLook, "lm-thing", inkOf(s.view, t.n))}
          {t.status === "cut" && <span className="nt-splice" title="you cut in here" aria-label="cut here">✂</span>}
          <Unwoven s={s} t={t} label={(n) => `${n} ${n === 1 ? "word" : "words"} cut from the reel`} />
          {live?.streaming && <span className="nt-caret" aria-hidden="true" />}
        </p>
        {live?.paused && <p className="nt-held">the projector holds while you type · enter cuts in here</p>}
        {live && <HurryHint s={s} className="nt-held" />}
        {looks?.map((l) => onLook && <CloseUp key={l.thing} s={s} l={l} onLook={onLook} />)}
        {!meta && !live && <Pull s={s} n={t.n} className="nt-pull" />}
        {!meta && !live && <Margins s={s} n={t.n} className="nt-margins" />}
        <ChapterEnd s={s} t={t} className="nt-chapter" />
      </div>
    </article>
  );
}

/** Now showing: the open threads, tap one to bring it back on your next line. */
function NowShowing({ s, threads, now, className = "" }: { s: StoryUI; threads: readonly ThreadView[]; now: number; className?: string }) {
  const shown = threadOrder(threads).filter((t) => t.state === "open").slice(0, 6);
  if (!shown.length) return null;
  return (
    <ul className={`nt-showing ${className}`} aria-label="Threads: tap one to bring it back on your next line">
      {shown.map((t) => {
        const quiet = quietFor(t, now) >= QUIET;
        return (
          <li key={t.id} title={t.hint}>
            {s.id
              ? <button type="button" className={`lm-pull${quiet ? " nt-quiet" : ""}`} data-fray={frayOf(t, now)} onClick={() => s.pull(t)} disabled={s.busy} title={`Bring it back: ${t.hint}`}>{t.name}</button>
              : <span>{t.name}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function Projector() {
  return <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M6 4l10 6-10 6z" /></svg>;
}

export function NitrateTheme({ s, home, skin, setSkin, goHome, openPlates, meta }: ThemeProps) {
  const view = s.view;
  const threads = view?.threads ?? [];
  const things = view?.things ?? [];
  const lookAt = (n: number) => (name: string) => s.look(n, name);

  const now = view?.n ?? s.live?.n ?? 0;
  const starting = !!home && !s.pending;
  const placeholder = s.live?.streaming ? "typing holds the projector; enter cuts in" : s.id ? "What do you do?" : "Begin your story…";

  return (
    <main className="sk-nitrate">
      <div className="nt-grain" aria-hidden="true" />
      <div className="nt-frame">
        <div className="nt-main">
          <header className="nt-head">
            <a className="nt-brand" href="/" aria-label={s.id ? "Your stories" : undefined} onClick={(e) => { e.preventDefault(); if (s.id) meta("stories"); else goHome(); }}>WEFTWAKE{s.id && <span className="lm-brand-caret" aria-hidden="true"> ▾</span>}</a>
            <span className="nt-title">{view ? <>{view.title}{now ? <> · turn {now}</> : null}</> : s.live ? `turn ${s.live.n}` : ""}</span>
            <SoundToggle className="nt-sound" />
            <SkinPicker skin={skin} setSkin={setSkin} className="nt-skins" />
            {view && <NowShowing s={s} threads={threads} now={now} className="nt-phone-only" />}
          </header>

          {starting && (
            <section className="nt-home">
              <p className="nt-lede">A picture that hasn't been shot yet. Write the first title card: what you do, where you are, what's true. The narrator films what happens next.</p>
              <p className="lm-what">Weftwake is a text adventure that writes itself as you play: you write what you do, an AI narrator writes what happens. Your first 40 turns are free.</p>
              <div className="nt-seeds" aria-label="Or begin with">
                {home.seeds.map((o) => (
                  <button key={o} type="button" onClick={() => s.send(o)} disabled={s.busy}>{o}</button>
                ))}
              </div>
            </section>
          )}

          <div className="nt-reel" role="log" aria-live="polite" aria-label="The story" aria-busy={!!s.live?.streaming || !!s.pending}>
            {view?.recap && s.turns.length > 0 && s.turns[0]!.n > 1 && (
              <p className="nt-recap nt-phone-only"><span className="nt-label">previously</span> {view.recap}</p>
            )}
            {s.loadFailed && (
              <p className="nt-notice">Couldn't open this story. <button type="button" onClick={s.reload}>Try again</button></p>
            )}
            {s.turns.map((t) => (
              <Fragment key={t.n}>
                <Scene s={s} t={t} things={things} looks={s.looks[t.n]} onLook={s.id ? lookAt(t.n) : null} />
                <Plates s={s} at={t.n} className="nt-still" />
              </Fragment>
            ))}
            {s.live && (
              <Scene
                s={s}
                t={{ n: s.live.n, input: s.live.input, prose: s.live.text, status: "streaming", intent: s.live.intent }}
                things={[]} onLook={null}
                live={{ streaming: s.live.streaming, paused: s.live.paused }}
              />
            )}
            {s.pending && !s.live && (
              <article className="nt-turn" data-pending><Card t={{ input: s.pending }} meta={false} /></article>
            )}
            {waiting(s) && <Weaving label="the projector starts…" className="nt-weaving" />}
            {/* The room the reply writes into (common.tsx ReadingRoom): nothing on screen moves while it's written. */}
            {s.id && <ReadingRoom s={s} writer=".nt-writer" />}
          </div>

          <Care s={s} className="nt-care" />
          {s.notice && <p className="nt-notice" role="status">{s.notice}</p>}

          {!s.resources && (
            <Writer
              s={s}
              className="nt-writer"
              label={s.id ? "Your next move" : "The first line of your story"}
              placeholder={placeholder}
              sendLabel={<Projector />}
            />
          )}

          {starting && home.stories.length > 0 && (
            <section className="nt-shelf" aria-label="Your stories">
              <h2>Your stories</h2>
              <ul>
                {home.stories.map((st) => (
                  <li key={st.id}>
                    <a href={`/s/${st.id}`} onClick={(e) => { e.preventDefault(); home.open(st.id); }}>{st.title}</a>
                    <span>{st.n} {st.n === 1 ? "turn" : "turns"}</span>
                    <DeleteStory title={st.title} remove={() => home.remove(st.id)} />
                  </li>
                ))}
              </ul>
              <p className="nt-label"><PlatesLink open={openPlates} label="your plates →" /></p>
            </section>
          )}

          {starting && <footer className="nt-foot nt-home-foot"><TrustLinks meta={meta} /></footer>}
          {starting && <Credit className="nt-credit" />}

          {s.id && (
            <footer className="nt-foot">
              <PlatesLink open={openPlates} />
              <button type="button" onClick={() => meta("stories")}>stories</button>
              <button type="button" onClick={() => meta("map")}>journal</button>
              <button type="button" onClick={() => meta("cloth")}>the cloth</button>
              <button type="button" onClick={() => meta("account")}>account</button>
              <button type="button" onClick={() => meta("feedback")}>feedback</button>
              <More>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>privacy</a>
                <a href="/api/export">export</a>
                <button type="button" onClick={() => meta("tour")}>how to play</button>
                <DreamNow s={s} />
                <button type="button" onClick={s.remove}>delete this story</button>
              </More>
            </footer>
          )}
        </div>

        {view && (
          <aside className="nt-side" aria-label="Beside the story">
            <WhereYouAre view={view} onLook={(name) => { const n = s.turns.at(-1)?.n; if (n) s.look(n, name); }} className="nt-where" />
            {threads.some((t) => t.state === "open") && (
              <section>
                <h2>Now showing</h2>
                <NowShowing s={s} threads={threads} now={now} className="nt-showing-side" />
              </section>
            )}
            {view.recap && (
              <section>
                <h2>Previously</h2>
                <p className="nt-side-recap">{view.recap}</p>
              </section>
            )}
          </aside>
        )}
      </div>
    </main>
  );
}
